import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ImagePlus,
  Loader2,
  Camera,
  Clock,
  ClipboardList,
  FileText,
  Package,
  Timer,
  Calculator,
  History,
  LayoutGrid,
  Sparkles,
  Wrench,
  FileWarning,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { getProjectTimeline } from "@/lib/site-visit.functions";
import { ProjectLinesTab } from "@/components/projects/ProjectLinesTab";
import { ProjectCalcTab } from "@/components/projects/ProjectCalcTab";
import { createQuoteFromProject } from "@/lib/create-quote-from-project";
import { projectTabKeys, projectsLabel, showsTradeDocumentation } from "@/modules/navigation";
import { SurveyBasisTab, SurveyReportTab } from "@/components/projects/SurveyBasisTab";
import { ProjectEconomyCard } from "@/components/projects/ProjectEconomyCard";
import { TakstAssignmentCard } from "@/components/projects/TakstAssignmentCard";
import { useProjectTakst } from "@/components/projects/use-project-takst";

import {
  PROJECT_IMAGE_BUCKET,
  signProjectImages,
  uploadProjectImage,
} from "@/lib/project-images";

export const Route = createFileRoute("/_authenticated/projects/$id")({
  component: ProjectDetail,
});

const TAB_META = {
  overview: { label: "Oversikt", icon: LayoutGrid },
  basis: { label: "Takstgrunnlag", icon: ClipboardList },
  visit: { label: "Befaring", icon: Camera },
  images: { label: "Bilder", icon: ImagePlus },
  materials: { label: "Materialer", icon: Package },
  hours: { label: "Timer", icon: Timer },
  standardjobs: { label: "Standardjobb", icon: Wrench },
  calc: { label: "Kalkyle", icon: Calculator },
  quotes: { label: "Tilbud", icon: FileText },
  report: { label: "Rapport", icon: FileWarning },
  history: { label: "Historikk", icon: History },
} as const;

type ProjectRow = {
  id: string;
  name: string;
  project_number: string | null;
  description: string | null;
  status: string;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  module_key: string;
  customer_id: string | null;
  company_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
};

function ProjectDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { companyId } = useActiveCompany();
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("overview");
  const { activeModuleKey } = useActiveCompany();

  const createQuote = useMutation({
    mutationFn: () => createQuoteFromProject(id),
    onSuccess: (res) => {
      toast.success(`Tilbud ${res.quoteNumber} opprettet`);
      qc.invalidateQueries({ queryKey: ["project-quotes", id] });
      qc.invalidateQueries({ queryKey: ["project-timeline", id] });
      navigate({ to: "/quotes/$id", params: { id: res.quoteId } });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke opprette tilbud"),
  });

  const projectQ = useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(
          "id, name, project_number, description, status, address, postal_code, city, module_key, customer_id, company_id, user_id, created_at, updated_at",
        )
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectRow | null;
    },
  });

  const project = projectQ.data;
  // Faner og dokumentasjon styres av modulen prosjektet tilhører.
  const projectModuleKey = project?.module_key ?? activeModuleKey;
  const tabKeys = projectTabKeys(projectModuleKey);
  const showDocumentation = showsTradeDocumentation(projectModuleKey);
  const isSurvey = tabKeys.includes("basis");
  // Takstoppdrag har egen veiviser – prosjektfanene er kun et desktop-oppslag.
  const takst = useProjectTakst(id);


  const customerQ = useQuery({
    queryKey: ["project-customer", project?.customer_id],
    enabled: !!project?.customer_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, name, email, phone")
        .eq("id", project!.customer_id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const ownerQ = useQuery({
    queryKey: ["project-owner", project?.user_id],
    enabled: !!project?.user_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, company_name")
        .eq("id", project!.user_id)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; email: string | null; company_name: string | null } | null;
    },
  });

  const imagesQ = useQuery({
    queryKey: ["project-images", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_images")
        .select("id, storage_path, caption, created_at")
        .eq("project_id", id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Kortlivede signerte URL-er slik at faktiske thumbnails kan vises.
  const imageUrlsQ = useQuery({
    queryKey: ["project-image-urls", id, (imagesQ.data ?? []).map((i) => i.id).join(",")],
    enabled: (imagesQ.data ?? []).length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const signed = await signProjectImages(
        supabase.storage.from(PROJECT_IMAGE_BUCKET) as never,
        imagesQ.data ?? [],
        600,
      );
      const map: Record<string, string> = {};
      for (const s of signed) if (s.url) map[s.row.id] = s.url;
      return map;
    },
  });

  const visitsQ = useQuery({
    queryKey: ["project-visits", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_visits")
        .select("id, status, created_at, updated_at")
        .eq("project_id", id)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  const quotesQ = useQuery({
    queryKey: ["project-quotes", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("id, quote_number, title, status, total_incl_vat, created_at")
        .eq("project_id", id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) throw new Error("Ikke innlogget");
      await uploadProjectImage({
        storage: supabase.storage.from(PROJECT_IMAGE_BUCKET) as never,
        companyId,
        projectId: id,
        file,
        register: async (path) => {
          const { error: insErr } = await supabase.from("project_images").insert({
            project_id: id,
            company_id: companyId,
            user_id: uid,
            storage_path: path,
            caption: file.name,
          });
          if (insErr) throw new Error(insErr.message);
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-images", id] });
      qc.invalidateQueries({ queryKey: ["project-image-urls", id] });
      qc.invalidateQueries({ queryKey: ["project-timeline", id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Opplasting feilet"),
  });

  // Dobbelttrykk skal ikke gi to like opplastinger: vi blokkerer mens en
  // opplasting pågår, og nullstiller feltet så samme fil kan velges på nytt
  // etter en feil.
  const onPick = async (files: FileList | null, input?: HTMLInputElement | null) => {
    if (!files || files.length === 0) return;
    if (uploading) return;
    const picked = Array.from(files);
    setUploading(true);
    let ok = 0;
    try {
      for (const f of picked) {
        await upload.mutateAsync(f);
        ok += 1;
      }
      toast.success(ok === 1 ? "Bildet er lagret på prosjektet" : `${ok} bilder lagret på prosjektet`);
    } catch {
      /* feilmelding vises av mutasjonen */
    } finally {
      setUploading(false);
      if (input) input.value = "";
    }
  };

  const timelineFn = useServerFn(getProjectTimeline);
  const timelineQ = useQuery({
    queryKey: ["project-timeline", id],
    queryFn: () => timelineFn({ data: { projectId: id } }),
  });

  const fmtDate = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtDateTime = (s?: string | null) =>
    s ? new Date(s).toLocaleString("nb-NO") : "—";
  const fmtMoney = (n: number | null | undefined) =>
    typeof n === "number"
      ? new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(n)
      : "—";

  const statusVariant = (s?: string | null) => {
    switch (s) {
      case "completed":
      case "ferdig":
        return "default" as const;
      case "on_hold":
      case "pause":
        return "secondary" as const;
      case "cancelled":
      case "kansellert":
        return "destructive" as const;
      default:
        return "outline" as const;
    }
  };

  return (
    <div className="mx-auto grid max-w-7xl gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_420px] lg:p-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/projects"><ArrowLeft className="mr-1 h-4 w-4" /> {projectsLabel(projectModuleKey)}</Link>
          </Button>
          <div className="flex items-center gap-2">
            {showDocumentation && (
              <Button asChild size="sm" variant="outline">
                <Link to="/projects/$id/documentation" params={{ id }}>
                  <ClipboardList className="mr-1 h-4 w-4" /> Dokumentasjon
                </Link>
              </Button>
            )}
            {isSurvey ? (
              <Button size="sm" onClick={takst.openTakst} disabled={takst.saving}>
                <ClipboardList className="mr-1 h-4 w-4" />
                {takst.saving ? "Oppretter…" : takst.label}
              </Button>
            ) : (
              <Button asChild size="sm">
                <Link to="/projects/$id/visit" params={{ id }}>
                  <Camera className="mr-1 h-4 w-4" /> Start befaring
                </Link>
              </Button>
            )}
          </div>
        </div>

        <Card className="p-5">
          {projectQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Laster prosjekt…</div>
          ) : !project ? (
            <div className="text-sm text-muted-foreground">Fant ikke prosjektet.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold leading-tight">{project.name}</h1>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{project.project_number ?? "Uten prosjektnummer"}</span>
                  </div>
                </div>
                <Badge variant={statusVariant(project.status)}>{project.status ?? "aktivt"}</Badge>
              </div>
            </div>
          )}
        </Card>

        {isSurvey && <TakstAssignmentCard projectId={id} />}

        <Tabs value={tab} onValueChange={setTab} className={isSurvey ? "hidden w-full lg:block" : "w-full"}>

          <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            {tabKeys.map((k) => {
              const meta = TAB_META[k];
              const Icon = meta.icon;
              return (
                <TabsTrigger key={k} value={k}>
                  <Icon className="mr-1 h-4 w-4" /> {meta.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* OVERSIKT */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <Card className="p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <InfoBlock label="Kunde" value={customerQ.data?.name ?? "Ingen kunde koblet"} />
                <InfoBlock
                  label="Adresse"
                  value={
                    project && (project.address || project.city)
                      ? [project.address, project.postal_code, project.city].filter(Boolean).join(", ")
                      : "—"
                  }
                />
                <InfoBlock label="Status" value={project?.status ?? "aktivt"} />
                <InfoBlock label="Opprettet" value={fmtDate(project?.created_at)} />
                <InfoBlock label="Sist oppdatert" value={fmtDate(project?.updated_at)} />
                <InfoBlock
                  label="Ansvarlig"
                  value={ownerQ.data?.email ?? "—"}
                />
                <InfoBlock
                  label="Kundekontakt"
                  value={
                    customerQ.data
                      ? [customerQ.data.email, customerQ.data.phone].filter(Boolean).join(" · ") || "—"
                      : "—"
                  }
                />
              </div>
              {project?.description && (
                <>
                  <Separator className="my-4" />
                  <div>
                    <div className="mb-1 text-xs font-medium text-muted-foreground">Beskrivelse</div>
                    <p className="whitespace-pre-wrap text-sm">{project.description}</p>
                  </div>
                </>
              )}
            </Card>

            <Card className="p-5">
              <div className="mb-3 text-sm font-semibold">Hurtighandlinger</div>
              <div className="flex flex-wrap gap-2">
                {isSurvey ? (
                  <Button size="sm" variant="outline" onClick={() => setTab("basis")}>
                    <ClipboardList className="mr-1 h-4 w-4" /> Takstgrunnlag
                  </Button>
                ) : null}
                <Button asChild size="sm">
                  <Link to="/projects/$id/visit" params={{ id }}>
                    <Camera className="mr-1 h-4 w-4" /> Ny befaring
                  </Link>
                </Button>

                <Button asChild size="sm" variant="outline" onClick={() => setTab("images")}>
                  <span><ImagePlus className="mr-1 h-4 w-4" /> Legg til bilder</span>
                </Button>
                {showDocumentation && (
                  <Button asChild size="sm" variant="outline">
                    <Link to="/projects/$id/documentation" params={{ id }}>
                      <ClipboardList className="mr-1 h-4 w-4" /> Dokumentasjon
                    </Link>
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setTab("history")}>
                  <History className="mr-1 h-4 w-4" /> Se historikk
                </Button>
                {tabKeys.includes("quotes") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => createQuote.mutate()}
                  disabled={createQuote.isPending}
                >
                  {createQuote.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-1 h-4 w-4" />
                  )}
                  Opprett tilbud fra prosjektet
                </Button>
                )}
              </div>
            </Card>


            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label="Bilder" value={imagesQ.data?.length ?? 0} icon={<ImagePlus className="h-4 w-4" />} />
              <StatCard label="Befaringer" value={visitsQ.data?.length ?? 0} icon={<Camera className="h-4 w-4" />} />
              {tabKeys.includes("quotes") && (
                <StatCard label="Tilbud" value={quotesQ.data?.length ?? 0} icon={<FileText className="h-4 w-4" />} />
              )}
            </div>

            {tabKeys.includes("quotes") && <ProjectEconomyCard projectId={id} />}

            {showDocumentation && (
              <Card className="p-5">
                <div className="mb-1 text-sm font-semibold">Dokumentasjon og FDV</div>
                <p className="mb-3 text-xs text-muted-foreground">
                  Risikovurdering, sluttkontroll, samsvarserklæring, FDV og kundepakke ligger i
                  fagets dokumentasjon. Ingenting markeres som utført eller signert automatisk.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline">
                    <Link to="/projects/$id/documentation" params={{ id }} search={{ tab: "hms" }}>
                      Risikovurdering (HMS)
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/projects/$id/documentation" params={{ id }} search={{ tab: "ks" }}>
                      Sluttkontroll og samsvar
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/projects/$id/documentation" params={{ id }} search={{ tab: "fdv" }}>
                      FDV og utstyr
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/projects/$id/documentation" params={{ id }} search={{ tab: "overview" }}>
                      Kundepakke og gjenstående
                    </Link>
                  </Button>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* BEFARING */}
          <TabsContent value="visit" className="mt-4">
            <Card className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Befaring</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Start en ny befaring, ta bilder og noter behov.
                  </p>
                </div>
                <Button asChild size="sm">
                  <Link to="/projects/$id/visit" params={{ id }}>
                    <Camera className="mr-1 h-4 w-4" /> Start ny befaring
                  </Link>
                </Button>
              </div>
              <Separator className="my-4" />
              {visitsQ.isLoading ? (
                <div className="text-sm text-muted-foreground">Laster…</div>
              ) : (visitsQ.data ?? []).length === 0 ? (
                <EmptyState
                  icon={<Camera className="h-5 w-5" />}
                  title="Ingen befaringer ennå"
                  body="Start en befaring for å samle notater og bilder på ett sted."
                />
              ) : (
                <ul className="space-y-2">
                  {visitsQ.data!.map((v) => (
                    <li
                      key={v.id}
                      className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium">Befaring · {v.status ?? "utkast"}</div>
                        <div className="text-xs text-muted-foreground">{fmtDateTime(v.created_at)}</div>
                      </div>
                      <Badge variant="outline">{v.status ?? "draft"}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </TabsContent>

          {/* BILDER */}
          <TabsContent value="images" className="mt-4">
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold">Bilder</div>
                <div>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => onPick(e.target.files, e.currentTarget)}
                  />
                  <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()} disabled={uploading}>
                    {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1 h-4 w-4" />}
                    Last opp
                  </Button>
                </div>
              </div>
              {(imagesQ.data ?? []).length === 0 ? (
                <EmptyState
                  icon={<ImagePlus className="h-5 w-5" />}
                  title="Ingen bilder ennå"
                  body="Bilder du legger til her samles på prosjektet."
                />
              ) : (
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {imagesQ.data!.map((img) => {
                    const url = imageUrlsQ.data?.[img.id];
                    return (
                      <li key={img.id} className="overflow-hidden rounded-md border border-border">
                        {url ? (
                          <a href={url} target="_blank" rel="noreferrer">
                            <img
                              src={url}
                              alt={img.caption ?? "Prosjektbilde"}
                              loading="lazy"
                              className="aspect-square w-full object-cover"
                            />
                          </a>
                        ) : (
                          <div className="grid aspect-square w-full place-items-center bg-muted text-xs text-muted-foreground">
                            {imageUrlsQ.isLoading ? "Laster…" : "Forhåndsvisning utilgjengelig"}
                          </div>
                        )}
                        <div className="truncate px-2 py-1 text-xs text-muted-foreground">
                          {img.caption ?? img.storage_path.split("/").pop()}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </TabsContent>

          {isSurvey && (
            <TabsContent value="basis" className="mt-4">
              <SurveyBasisTab
                projectId={id}
                customerName={customerQ.data?.name ?? "Ingen oppdragsgiver koblet"}
                address={
                  project && (project.address || project.city)
                    ? [project.address, project.postal_code, project.city].filter(Boolean).join(", ")
                    : "—"
                }
              />
            </TabsContent>
          )}

          {isSurvey && (
            <TabsContent value="report" className="mt-4">
              <SurveyReportTab projectId={id} />
            </TabsContent>
          )}

          {/* MATERIALER */}
          {tabKeys.includes("materials") && (
          <TabsContent value="materials" className="mt-4">
            {project && companyId ? (
              <ProjectLinesTab
                projectId={id}
                companyId={companyId}
                moduleKey={project.module_key}
                kind="materials"
              />
            ) : (
              <Card className="p-5 text-sm text-muted-foreground">Laster…</Card>
            )}
          </TabsContent>
          )}

          {/* TIMER */}
          {tabKeys.includes("hours") && (
          <TabsContent value="hours" className="mt-4">
            {project && companyId ? (
              <ProjectLinesTab
                projectId={id}
                companyId={companyId}
                moduleKey={project.module_key}
                kind="hours"
              />
            ) : (
              <Card className="p-5 text-sm text-muted-foreground">Laster…</Card>
            )}
          </TabsContent>
          )}

          {/* STANDARDJOBBER */}
          {tabKeys.includes("standardjobs") && (
          <TabsContent value="standardjobs" className="mt-4">
            {project && companyId ? (
              <ProjectLinesTab
                projectId={id}
                companyId={companyId}
                moduleKey={project.module_key}
                kind="standardjobs"
                userId={project.user_id}
              />
            ) : (
              <Card className="p-5 text-sm text-muted-foreground">Laster…</Card>
            )}
          </TabsContent>
          )}

          {/* KALKYLE */}
          {tabKeys.includes("calc") && (
          <TabsContent value="calc" className="mt-4">
            <ProjectCalcTab projectId={id} />
          </TabsContent>
          )}

          {/* TILBUD */}
          {tabKeys.includes("quotes") && (
          <TabsContent value="quotes" className="mt-4">
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">Tilbud knyttet til prosjektet</div>
                <Button
                  size="sm"
                  onClick={() => createQuote.mutate()}
                  disabled={createQuote.isPending}
                >
                  {createQuote.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-1 h-4 w-4" />
                  )}
                  Opprett tilbud fra prosjektet
                </Button>
              </div>
              {quotesQ.isLoading ? (
                <div className="text-sm text-muted-foreground">Laster…</div>
              ) : (quotesQ.data ?? []).length === 0 ? (
                <EmptyState
                  icon={<FileText className="h-5 w-5" />}
                  title="Ingen tilbud ennå"
                  body="Klikk «Opprett tilbud fra prosjektet» for å overføre materialer og timer til et nytt tilbud i status kladd."
                />
              ) : (
                <ul className="divide-y divide-border">
                  {quotesQ.data!.map((q) => (
                    <li key={q.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <Link
                          to="/quotes/$id"
                          params={{ id: q.id }}
                          className="font-medium hover:underline"
                        >
                          {q.quote_number ?? "Uten nummer"} · {q.title ?? "Uten tittel"}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {fmtDate(q.created_at)} · {q.status ?? "kladd"}
                        </div>
                      </div>
                      <div className="shrink-0 text-sm font-semibold">{fmtMoney(q.total_incl_vat)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </TabsContent>
          )}


          {/* HISTORIKK */}
          <TabsContent value="history" className="mt-4">
            <Card className="p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Clock className="h-4 w-4" /> Tidslinje
              </div>
              {timelineQ.isLoading ? (
                <div className="text-sm text-muted-foreground">Laster…</div>
              ) : (timelineQ.data?.events ?? []).length === 0 ? (
                <EmptyState
                  icon={<History className="h-5 w-5" />}
                  title="Ingen hendelser ennå"
                  body="Bilder, befaringer og tilbud dukker opp her etter hvert som du jobber i prosjektet."
                />
              ) : (
                <ol className="space-y-3">
                  {timelineQ.data!.events.map(
                    (ev: { id: string; title: string; description: string | null; created_at: string }) => (
                      <li key={ev.id} className="flex items-start gap-3 text-sm">
                        <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{ev.title}</div>
                          {ev.description && (
                            <div className="text-xs text-muted-foreground">{ev.description}</div>
                          )}
                          <div className="text-xs text-muted-foreground">{fmtDateTime(ev.created_at)}</div>
                        </div>
                      </li>
                    ),
                  )}
                </ol>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Hurtighandlinger på mobil – timer, materiell og bilde rett i prosjektet. */}
      {!isSurvey && (
        <>
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => onPick(e.target.files, e.currentTarget)}
          />
          <div className="h-16 lg:hidden" aria-hidden />
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-2 backdrop-blur lg:hidden">
            <div className="grid grid-cols-3 gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!tabKeys.includes("hours")}
                onClick={() => setTab("hours")}
              >
                <Timer className="mr-1 h-4 w-4" /> Timer
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!tabKeys.includes("materials")}
                onClick={() => setTab("materials")}
              >
                <Package className="mr-1 h-4 w-4" /> Materiell
              </Button>
              <Button size="sm" disabled={uploading} onClick={() => cameraInput.current?.click()}>
                {uploading ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="mr-1 h-4 w-4" />
                )}
                {uploading ? "Lagrer…" : "Bilde"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="text-muted-foreground">{icon}</div>
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </Card>
  );
}

function EmptyState({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border/70 px-4 py-8 text-center">
      <div className="text-muted-foreground">{icon}</div>
      <div className="text-sm font-medium">{title}</div>
      <p className="max-w-md text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

function ComingSoonCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-start gap-4">
        <div className="rounded-md bg-primary/10 p-2 text-primary">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold">{title}</div>
            <Badge variant="secondary" className="gap-1">
              <Sparkles className="h-3 w-3" /> Kommer snart
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{body}</p>
        </div>
      </div>
    </Card>
  );
}
